import { DateRangeValue } from '@elementary/common-ui/components/DateRangePicker';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { gql } from 'graphql-request';
import { SourceType } from 'shared/types';
import {
  AssetExecutionHistory,
  TestOverviewExecutionHistory,
} from 'shared/types/execution';

export const getTestOverviewExecutionHistoryQuery = gql`
  query GetTestOverviewExecutionHistory(
    $id: String!
    $executionHistoryStartTime: DateTime!
    $executionHistoryEndTime: DateTime!
  ) {
    latestTestExecution(id: $id) {
      test {
        id
        executionHistory(
          startTime: $executionHistoryStartTime
          endTime: $executionHistoryEndTime
          subTestUniqueId: $id
        ) {
          id
          executionTimestamp
          description
          status
          failures
          exception
        }
      }
    }
  }
`;

export const getAssetExecutionHistoryQuery = gql`
  query GetAssetExecutionHistory(
    $id: String!
    $executionHistoryStartTime: DateTime!
    $executionHistoryEndTime: DateTime!
  ) {
    asset(id: $id) {
      ... on TableAsset {
        id
        executionHistory(
          startTime: $executionHistoryStartTime
          endTime: $executionHistoryEndTime
        ) {
          id
          executionTimestamp
          status
          exception
        }
      }
      ... on EtlAsset {
        id
        executionHistory(
          startTime: $executionHistoryStartTime
          endTime: $executionHistoryEndTime
        ) {
          id
          executionTimestamp
          status
          exception
        }
      }
    }
  }
`;

function useTestOverviewExecutionHistory(
  dateRange: DateRangeValue,
  id: string,
  sourceType: SourceType,
) {
  const { data, isFetching, error } = useGraphqlQuery<
    | {
        asset: AssetExecutionHistory;
      }
    | { latestTestExecution: { test: TestOverviewExecutionHistory } }
  >({
    queryKey: [
      'testOverviewSidePanelExecutionHistory',
      id,
      sourceType,
      dateRange.start?.format(),
      dateRange.end?.format(),
    ],
    query:
      sourceType === SourceType.TEST
        ? getTestOverviewExecutionHistoryQuery
        : getAssetExecutionHistoryQuery,
    variables: {
      id,
      executionHistoryStartTime: dateRange.start?.startOf('d').format(),
      executionHistoryEndTime: dateRange.end?.endOf('d').format(),
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  return {
    isFetching,
    data:
      data === undefined
        ? undefined
        : sourceType === SourceType.TEST
        ? (
            data as {
              latestTestExecution: { test: TestOverviewExecutionHistory };
            }
          ).latestTestExecution.test
        : (data as { asset: AssetExecutionHistory }).asset,
    error,
  };
}

export default useTestOverviewExecutionHistory;
